import { ApiErrorCode, ApiResponse, withApiErrorHandler } from '@/modules/common/api/errors';
import { axiosInstance } from '@/utils/axios';

import {
  AssignmentResponse,
  CreateAssignmentRequest,
  CutAssignmentRequest,
  DeleteAssignmentRequest,
  DuplicateAssignmentRequest,
  PreviewAssignmentBookedMinutesRequest,
  PreviewAssignmentBookedMinutesResponse,
  UpdateAssignmentRequest,
  ValidateAssignmentRequest,
  ValidateAssignmentResponse,
} from '../types/dto';

/**
 * Creates a new assignment.
 * POST /api/assignments
 *
 * @param payload - Assignment creation data
 * @returns Created assignment
 */
export const createAssignment = async (
  payload: CreateAssignmentRequest
): Promise<AssignmentResponse> => {
  const endpoint = '/api/assignments';

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.ASSIGNMENT_CREATE_FAILED, () =>
    axiosInstance.post<{
      data: AssignmentResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, payload)
  );

  return apiData;
};

/**
 * Updates an existing assignment.
 * PATCH /api/assignments
 *
 * @param payload - Assignment update data including assignmentId
 * @returns Updated assignment
 */
export const updateAssignment = async (
  payload: UpdateAssignmentRequest
): Promise<AssignmentResponse> => {
  const endpoint = '/api/assignments';

  return await withApiErrorHandler(endpoint, ApiErrorCode.ASSIGNMENT_UPDATE_FAILED, () =>
    axiosInstance.patch<{
      data: AssignmentResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, payload)
  );
};

/**
 * Validates an assignment to check for overlaps with existing assignments.
 * Used to prevent scheduling conflicts for users.
 *
 * @param payload - Assignment data to validate
 * @returns Validation result indicating if assignment overlaps
 */
export const validateAssignment = async (
  payload: ValidateAssignmentRequest
): Promise<ValidateAssignmentResponse> => {
  const endpoint = '/api/assignments/validate';

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.ASSIGNMENT_VALIDATION_FAILED,
    () =>
      axiosInstance.post<{
        data: ValidateAssignmentResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, payload)
  );

  return response;
};

/**
 * Booked minutes an unsaved assignment would have, net of approved time off.
 * POST /api/assignments/booked-minutes/preview
 */
export const previewAssignmentBookedMinutes = async (
  payload: PreviewAssignmentBookedMinutesRequest
): Promise<PreviewAssignmentBookedMinutesResponse> => {
  const endpoint = '/api/assignments/booked-minutes/preview';

  return await withApiErrorHandler(
    endpoint,
    ApiErrorCode.ASSIGNMENT_BOOKED_MINUTES_PREVIEW_FAILED,
    () => axiosInstance.post<ApiResponse<PreviewAssignmentBookedMinutesResponse>>(endpoint, payload)
  );
};

/**
 * Deletes an assignment.
 * DELETE /api/assignments
 *
 * @param payload - Assignment ID to delete
 */
export const deleteAssignment = async (payload: DeleteAssignmentRequest): Promise<void> => {
  const endpoint = '/api/assignments';

  await withApiErrorHandler(endpoint, ApiErrorCode.ASSIGNMENT_DELETE_FAILED, () =>
    axiosInstance.delete(endpoint, { data: payload })
  );
};

/**
 * Cuts an assignment into two parts at the specified date.
 * POST /api/assignments/cut
 *
 * The assignment is split at the cutDate:
 * - First part: original assignment from startDate to (cutDate - 1 day)
 * - Second part: new assignment from cutDate to endDate
 *
 * @param payload - Assignment ID, cut date, and optional sync token
 */
export const cutAssignment = async (payload: CutAssignmentRequest): Promise<void> => {
  const endpoint = '/api/assignments/cut';

  await withApiErrorHandler(endpoint, ApiErrorCode.ASSIGNMENT_CUT_FAILED, () =>
    axiosInstance.post(endpoint, payload)
  );
};

/**
 * Duplicates an existing assignment with a new date range.
 * POST /api/assignments/duplicate
 *
 * The backend copies all fields from the source assignment, applying
 * the provided startDate and endDate to the new copy.
 *
 * @param payload - Source assignment ID plus the new start and end dates
 * @returns The newly created duplicate assignment
 */
export const duplicateAssignment = async (
  payload: DuplicateAssignmentRequest
): Promise<AssignmentResponse> => {
  const endpoint = '/api/assignments/duplicate';

  return await withApiErrorHandler(endpoint, ApiErrorCode.ASSIGNMENT_DUPLICATE_FAILED, () =>
    axiosInstance.post<{
      data: AssignmentResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, payload)
  );
};
